import z from 'zod'

export type BrandMetric = {
  p50: number
  p75: number
  p25: number
  avg: number
  min: number
  max: number
  maad: number
  measure: string
}

export type VenueMeasures = {
  venueId: string
  totalPopulation: number
  totalSpend: number
  foodSpend: number
  alcoholSpend: number
  apparelSpend: number
  entertainmentSpend: number
  medianAge: number
  avgAge: number
  avgHomeValue: number
  medianHomeValue: number
  avgHouseholdIncome: number
  medianHouseholdIncome: number
  whitePopulation: number
}

export type BrandDistance = {
  id: string
  brand: string
  count: number
  naicsCode: string
  distanceMeters: number
  nearestState: string
  logoUrl: string
  metrics: BrandMetric[]
  squareFeet: BrandMetric
}

export type NaicsCode = {
  code: string
  label: string
}

export type VoidMeasure = {
  name: string
  key: string
  chartRange: [number, number]
  type: 'sum' | 'avg' | 'med'
  col: string
  cols?: string[]
  childMetric?: boolean
  // if synthetic we don't query for it
  synthetic?: boolean
  step: number
}

export const voidMeasures: Record<VoidMeasure['key'], VoidMeasure> = {
  totalPopulation: {
    name: 'Total Population',
    key: 'totalPopulation',
    chartRange: [0, 20_000],
    step: 5_000,
    type: 'sum',
    col: 'cy_pop_c',
  },
  medianHouseholdIncome: {
    name: 'Median Household Income',
    key: 'medianHouseholdIncome',
    chartRange: [50_000, 100_000],
    step: 10_000,
    type: 'med',
    col: 'cy_med_hinc_c',
  },
  avgAge: {
    name: 'Average Age',
    key: 'avgAge',
    chartRange: [35, 45],
    step: 1,
    type: 'avg',
    col: 'cy_av_age_c',
  },
  medianHomeValue: {
    name: 'Median Home Value',
    key: 'medianHomeValue',
    chartRange: [200_000, 400_000],
    step: 25_000,
    type: 'med',
    col: 'cy_med_valuec',
  },
  totalEducation: {
    name: 'Education',
    key: 'totalEducation',
    chartRange: [0, 20_000],
    step: 5_000,
    type: 'sum',
    col: '',
    synthetic: true,
  },
  race: {
    name: 'Race & Ethnicity',
    key: 'race',
    chartRange: [0, 20_000],
    step: 5_000,
    type: 'sum',
    col: '',
    synthetic: true,
  },
  whitePopulation: {
    name: 'White Population',
    key: 'whitePopulation',
    chartRange: [3_000, 9_000],
    step: 1_000,
    type: 'sum',
    col: 'cy_white_c',
    childMetric: true,
  },
  blackPopulation: {
    name: 'Black Population',
    key: 'blackPopulation',
    chartRange: [0, 1_500],
    step: 250,
    type: 'sum',
    childMetric: true,
    col: 'cy_black_c',
  },
  hispanicPopulation: {
    name: 'Hispanic Population',
    key: 'hispanicPopulation',
    chartRange: [0, 2_500],
    step: 500,
    type: 'sum',
    col: 'cy_hispanic_c',
    childMetric: true,
  },
  asianPopulation: {
    name: 'Asian Population',
    key: 'asianPopulation',
    chartRange: [0, 1_000],
    step: 250,
    type: 'sum',
    col: 'cy_asian_c',
    childMetric: true,
  },
  otherPopulation: {
    name: 'Other Population',
    key: 'otherPopulation',
    chartRange: [0, 1_750],
    step: 250,
    type: 'sum',
    col: '',
    childMetric: true,
    // actual other, pacific islander, indian, and 2+ races
    cols: ['cy_other_c', 'cy_pi_c', 'cy_indian_c', 'cy_two_plus_c'],
  },
  educationSomeHS: {
    name: 'No High School Degree',
    key: 'educationSomeHS',
    chartRange: [0, 1_000],
    step: 100,
    type: 'sum',
    col: 'cy_edu_sm_hsc',
    cols: ['cy_edu_sm_hsc', 'cy_edu_l9_c'],
    childMetric: true,
  },
  educationHS: {
    name: 'High School Graduate',
    key: 'educationHS',
    chartRange: [500, 2_000],
    step: 250,
    type: 'sum',
    col: 'cy_edu_hs_c',
    childMetric: true,
  },
  educationSomeCollege: {
    name: 'Some College, No Degree',
    key: 'educationSomeCollege',
    chartRange: [500, 2_000],
    step: 250,
    type: 'sum',
    col: 'cy_edu_smcolc',
    childMetric: true,
  },
  bachelors: {
    name: 'Undergraduate Degree',
    key: 'bachelors',
    chartRange: [500, 1_500],
    step: 250,
    type: 'sum',
    col: '',
    // associates + bachelor
    cols: ['cy_edu_ba_c', 'cy_edu_as_c'],
    childMetric: true,
  },
  graduate: {
    name: 'Graduate Degree',
    key: 'graduate',
    chartRange: [0, 1_500],
    step: 250,
    type: 'sum',
    col: '',
    // masters + professional + doctorate
    cols: ['cy_edu_mast_c', 'cy_edu_prof_c', 'cy_edu_doct_c'],
    childMetric: true,
  },
}
export const personContactInfoSchema = z.object({
  brandId: z.string().nonempty(),
  companyName: z.string().nonempty(),
  contactId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  jobTitle: z.string(),
  jobFunction: z.string(),
  managementLevel: z.string(),
  linkedInUrl: z.string(),
  directPhoneNumber: z.string(),
  mobilePhoneNumber: z.string(),
  otherPhoneNumber: z.string(),
  email1: z.string(),
  email2: z.string(),
  email3: z.string(),
  department: z.string(),
  personStreet: z.string(),
  personCity: z.string(),
  personState: z.string(),
  personZip: z.string(),
  country: z.string(),
})

export type PersonContactInfo = z.infer<typeof personContactInfoSchema>

export const personContactInfoCountSchema = z.object({
  brandId: z.string().nonempty(),
  count: z.number(),
})

export type PersonContactInfoCount = z.infer<typeof personContactInfoCountSchema>

export const brandContactInfoSchema = z.object({
  brandId: z.string().nonempty(),
  companyName: z.string().nonempty(),
  alphamapCategory: z.string(),
  ambiguousSearchWord: z.string(),
  companyName2: z.string(),
  companyWebsite: z.string(),
  companyCountry: z.string(),
  companyPhone: z.string(),
  companyUSWebsite: z.string(),
  companyUSPhone: z.string(),
  companyUSAddress: z.string(),
  companyUSLinkedin: z.string(),
  realEstateLeasingWebsite: z.string(),
  realEstateLeasingPhone: z.string(),
  realEstateLeasingEmail: z.string(),
  contactUsPage: z.string(),
  submitASitePage: z.string(),
  systemDataMatch: z.string(),
  notes: z.string(),
  fullAddress: z.string(),
  linkedinUrl: z.string(),
  numberOfLocations: z.number(),
  country: z.string(),
})
export type BrandContactInfo = z.infer<typeof brandContactInfoSchema>

export type BrandsContactInfo = Record<string, BrandContactInfo>

export type BrandsPeopleContactInfo = Record<string, PersonContactInfo[]>

export type BrandsContactInfoSummary = Record<string, number>
